import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  ExternalLink,
  FileCheck2,
  Info,
  Scale,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import './AiOwnershipAgreementPage.css';

const EFFECTIVE_DATE = '2026年9月3日';
const OWNERSHIP_PDF_PATH = '/docs/shadonggua-ai-ownership-agreement.pdf';

const sections = [
  { id: 'scope', label: '一、适用范围与定义' },
  { id: 'generated-rights', label: '二、生成内容的知识产权与使用权' },
  { id: 'input-rights', label: '三、输入内容的权利与授权保证' },
  { id: 'platform-rights', label: '四、平台软件与知识产权边界' },
  { id: 'team-rights', label: '五、团队与委托创作' },
  { id: 'service-license', label: '六、必要的处理许可' },
  { id: 'third-party', label: '七、第三方服务与素材' },
  { id: 'publishing', label: '八、使用、发布与标识' },
  { id: 'records', label: '九、生成记录与证明效力' },
  { id: 'complaints', label: '十、投诉与其他事项' },
];

const LegalSourceLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="ownership-source-link"
  >
    <span>{children}</span>
    <ExternalLink size={14} aria-hidden="true" />
  </a>
);

const Section = ({ id, title, children }) => (
  <section id={id} className="ownership-section" aria-labelledby={`${id}-title`}>
    <h2 id={`${id}-title`}>{title}</h2>
    <div className="ownership-section__body">{children}</div>
  </section>
);

const AiOwnershipAgreementPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'AI 软件及生成内容权属说明 - 沙东瓜';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="ownership-page">
      <header className="ownership-topbar ownership-screen-only">
        <div className="ownership-topbar__inner">
          <Link to="/" className="ownership-brand" aria-label="返回沙东瓜首页">
            <span className="ownership-brand__mark" aria-hidden="true">
              <FileCheck2 size={19} />
            </span>
            <span>沙东瓜</span>
          </Link>
          <div className="ownership-topbar__actions">
            <Link to="/" className="ownership-action-button ownership-action-button--quiet">
              <ArrowLeft size={17} aria-hidden="true" />
              <span>返回工作台</span>
            </Link>
            <a
              href={OWNERSHIP_PDF_PATH}
              download="沙东瓜-AI软件及生成内容权属说明.pdf"
              className="ownership-action-button ownership-action-button--primary"
            >
              <Download size={17} aria-hidden="true" />
              <span>保存 PDF</span>
            </a>
          </div>
        </div>
      </header>

      <main className="ownership-layout">
        <aside className="ownership-toc ownership-screen-only" aria-label="本页目录">
          <p className="ownership-toc__label">本页目录</p>
          <nav>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                {section.label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="ownership-document">
          <header className="ownership-hero">
            <div className="ownership-hero__eyebrow">
              <ShieldCheck size={16} aria-hidden="true" />
              <span>用户权益与知识产权说明</span>
            </div>
            <h1>沙东瓜 AI 软件及生成内容权属说明</h1>
            <p className="ownership-hero__intro">
              本说明用于明确用户使用沙东瓜 AI 创作功能时，输入素材、生成内容、平台软件及第三方权利之间的边界。
            </p>
            <p className="ownership-effective-date">生效日期：{EFFECTIVE_DATE}</p>
          </header>

          <section className="ownership-core" aria-labelledby="ownership-core-title">
            <div className="ownership-core__icon" aria-hidden="true">
              <Scale size={23} />
            </div>
            <div>
              <p className="ownership-core__label">核心权属结论</p>
              <h2 id="ownership-core-title">
                沙东瓜不因提供 AI 生成或处理服务而取得用户合法内容的所有权。
              </h2>
              <p>
                在用户与沙东瓜之间，用户对其合法输入内容原有的权利保持不变；对依法属于用户的生成内容，沙东瓜不主张所有权，也不以平台自身权利限制用户依法使用或商业使用。
              </p>
            </div>
          </section>

          <div className="ownership-mobile-toc ownership-screen-only">
            <details>
              <summary>查看本页目录</summary>
              <nav aria-label="移动端目录">
                {sections.map((section) => (
                  <a key={section.id} href={`#${section.id}`}>{section.label}</a>
                ))}
              </nav>
            </details>
          </div>

          <div className="ownership-content">
            <Section id="scope" title="一、适用范围与定义">
              <p>
                本说明适用于沙东瓜网站及其提供的剧本处理、角色与场景设计、图片、音频、视频、分镜及其他 AI 生成或编辑功能。具体开放能力以用户实际使用的产品页面为准。
              </p>
              <p>
                用户提交、上传、填写或绑定的文字、剧本、提示词、图片、音频、视频及其他素材，统称为“输入内容”；平台根据用户指令生成、编辑或处理形成的内容，统称为“生成内容”；沙东瓜的软件代码、界面、产品设计、工作流、系统提示模板、文档和品牌标识，统称为“平台内容”。
              </p>
            </Section>

            <Section id="generated-rights" title="二、生成内容的知识产权与使用权">
              <p className="ownership-emphasis">
                在用户与沙东瓜之间，对于依法归属于用户的生成内容，其知识产权及其他相关财产性权益由用户享有和行使；沙东瓜不因提供 AI 生成、编辑或处理服务而取得该生成内容的所有权。
              </p>
              <p>
                AI 生成内容是否构成受著作权法保护的作品，需要结合用户的实际智力投入、个性化选择、调整过程和最终表达进行个案判断。平台不保证每项生成内容当然取得著作权，也不保证生成结果具有唯一性、排他性或绝不侵权。
              </p>
              <div className="ownership-note ownership-note--info">
                <Info size={18} aria-hidden="true" />
                <p>
                  在遵守法律法规、第三方素材许可及所选模型服务规则的前提下，沙东瓜自身不对用户合法使用或商业使用生成内容另设“仅限非商业使用”的限制。
                </p>
              </div>
            </Section>

            <Section id="input-rights" title="三、输入内容的权利与授权保证">
              <p className="ownership-emphasis">
                用户上传或提交输入内容，不代表其所有权或知识产权转让给沙东瓜。用户或原权利人对输入内容依法享有的权利保持不变。
              </p>
              <p>
                用户应确保其对输入内容拥有相应权利或已取得充分、有效的授权，包括但不限于著作权、商标权、专利权，以及姓名、肖像、声音、名誉、隐私和个人信息等权益。将第三方内容作为参考素材，不会使用户自动取得该第三方内容或其中元素的权利。
              </p>
            </Section>

            <Section id="platform-rights" title="四、平台软件与知识产权边界">
              <p>
                除用户内容及已明确标注的第三方内容外，沙东瓜 AI 软件的程序代码、界面设计、产品架构、通用工作流、系统模板、技术文档、品牌标识及其他平台内容，其知识产权及相关权益归平台运营方或相应许可方所有。用户取得某项生成内容，不代表同时取得上述平台内容或第三方模型的权利。
              </p>
              <p>
                未经许可，用户不得复制、出售、出租、反向工程或以其他方式不当使用平台内容；法律允许且不得通过协议排除的情形除外。
              </p>
            </Section>

            <Section id="team-rights" title="五、团队、委托与职务创作">
              <p>
                任务发起账号、项目创建人或费用支付人不当然等同于最终权利人。涉及团队协作、客户委托、员工职务创作、联合制作或外包制作时，生成内容的最终归属应依照法律及相关方之间的合同确定。沙东瓜仅记录平台内的操作与生成事实，不代替相关方裁决权属争议。
              </p>
            </Section>

            <Section id="service-license" title="六、完成服务所必要的处理许可">
              <p>
                为执行用户选择的功能，用户授予沙东瓜一项有限、非独占、仅为提供服务所必要的许可，使平台可以对输入内容和生成内容进行上传、缓存、存储、格式转换、传输至用户选定的第三方服务商、结果交付、安全合规检查、故障排查及客户支持。
              </p>
              <p>
                除用户主动发布至公开区域、产品功能已有清楚说明，或用户另行明确同意外，沙东瓜不会将用户内容用于公开展示、广告宣传或训练沙东瓜自有、专用模型。第三方服务商对数据的处理规则，以生成页面披露及其适用条款为准。
              </p>
            </Section>

            <Section id="third-party" title="七、第三方服务与素材">
              <p>
                沙东瓜可能接入不同的模型、云服务、代理服务、字体、音色或素材供应商。第三方服务可能有独立的使用条件、商用范围、地域限制、数据处理或标识要求；沙东瓜无法授予超出第三方权利人授权范围的权利。
              </p>
              <p>
                生成内容中如包含他人既有作品、角色形象、商标、字体、音乐、肖像、声音或其他受保护元素，相关权利仍归原权利人。用户在发布或商业使用前，应根据实际用途完成必要核验与授权。
              </p>
            </Section>

            <Section id="publishing" title="八、使用、发布与 AI 标识">
              <p>
                用户可以在其依法享有权利或已获授权的范围内使用、修改、复制、发行、传播或商业化生成内容，并对具体发布行为及其后果承担相应责任。
              </p>
              <p>
                平台会按照适用法律法规和国家标准，为生成合成内容添加显式或隐式标识。用户对外发布时应履行适用的声明或标识义务，不得恶意删除、篡改、伪造或隐匿依法添加的标识。
              </p>
            </Section>

            <Section id="records" title="九、生成记录与证明效力">
              <p>
                在系统实际留存范围内，沙东瓜的项目、任务、账号、模型、提示词、参考素材、生成时间、输出文件及操作记录，可以用于说明相关内容在平台内的创作与生成过程。具体记录以平台数据库和原始文件为准。
              </p>
              <div className="ownership-note ownership-note--warning">
                <TriangleAlert size={18} aria-hidden="true" />
                <p>
                  本说明仅证明沙东瓜与用户之间的权利安排，不是著作权登记证书、公证书，也不替代行政机关或司法机关对某项内容的独创性、真实权属或侵权与否作出的认定。
                </p>
              </div>
              <p>
                对单项内容需要留存证据时，建议同时保存原始输入、提示词版本、参考素材授权、生成任务编号、模型与服务商、原始输出文件、修改与选片记录，以及可验证的文件哈希和时间信息。
              </p>
            </Section>

            <Section id="complaints" title="十、投诉、处理与其他事项">
              <p>
                如权利人认为平台内的内容侵犯其合法权益，可以通过沙东瓜公布的客服或意见反馈渠道提交权利证明、内容定位信息及具体诉求。平台将依法核验，并根据情况采取限制访问、停止处理、删除或保存相关记录等措施。
              </p>
              <p>
                因法律法规、监管要求、产品能力或第三方服务规则变化，本说明可能适时更新。涉及用户核心权利的重大变化，平台将通过合理方式提示。更新后的版本自公示的生效日期起适用。
              </p>
            </Section>

            <section className="ownership-basis" aria-labelledby="ownership-basis-title">
              <div className="ownership-basis__heading">
                <CheckCircle2 size={19} aria-hidden="true" />
                <h2 id="ownership-basis-title">主要规则依据</h2>
              </div>
              <div className="ownership-source-grid">
                <LegalSourceLink href="https://www.npc.gov.cn/c2/c30834/202011/t20201119_308796.html">
                  《中华人民共和国著作权法》
                </LegalSourceLink>
                <LegalSourceLink href="https://www.cac.gov.cn/2023-07/13/c_1690898327029107.htm">
                  《生成式人工智能服务管理暂行办法》
                </LegalSourceLink>
                <LegalSourceLink href="https://www.cac.gov.cn/2022-12/11/c_1672221949354811.htm">
                  《互联网信息服务深度合成管理规定》
                </LegalSourceLink>
                <LegalSourceLink href="https://www.cac.gov.cn/2025-03/14/c_1743654684782215.htm">
                  《人工智能生成合成内容标识办法》
                </LegalSourceLink>
              </div>
            </section>
          </div>

          <footer className="ownership-document__footer">
            <p>沙东瓜 AI 软件及生成内容权属说明</p>
            <p>生效于 {EFFECTIVE_DATE}</p>
          </footer>
        </article>
      </main>
    </div>
  );
};

export default AiOwnershipAgreementPage;
